import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

// A list screen's filters, kept in the URL query string instead of component state, so a filtered
// view can be shared as a link and the browser's Back button returns to it. `keys` names every
// filter the screen owns; an empty value is removed from the URL rather than written as `?status=`.
//
// The last filters are also remembered per list for the browser tab (sessionStorage). Detail pages
// return with their own back arrow or a plain navigate('/list'), which carries no query string;
// opening the list without any filter in the URL restores the remembered ones, so every way back
// lands on the same filtered list. "Clear filters" forgets them. A new tab starts clean.
//
// Updates replace the history entry, so typing in the search box does not leave one Back step per
// keystroke.
const storageKeyFor = (pathname) => `listFilters:${pathname}`;

const readStored = (key) => {
  try {
    return window.sessionStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeStored = (key, value) => {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage blocked (private mode, policy): the URL still carries the filters.
  }
};

export default function useUrlFilters(keys) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const storageKey = storageKeyFor(pathname);
  const keyList = keys.join('|');

  const filters = useMemo(
    () => Object.fromEntries(keys.map((key) => [key, searchParams.get(key) || ''])),
    // keys is a constant array per screen; joining keeps the memo stable if a caller inlines it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, keyList]
  );

  // Only the screen's own filters are remembered and compared, never unrelated query parameters.
  const ownQuery = useMemo(() => {
    const own = new URLSearchParams();
    keys.forEach((key) => { if (filters[key]) own.set(key, filters[key]); });
    return own.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, keyList]);

  // Arriving with no filter in the URL: bring back the remembered ones before the first paint.
  useLayoutEffect(() => {
    if (ownQuery) return;
    const stored = readStored(storageKey);
    if (!stored) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        new URLSearchParams(stored).forEach((value, key) => {
          if (keys.includes(key)) next.set(key, value);
        });
        return next;
      },
      { replace: true }
    );
    // Runs once per visit to the list: later changes are the user's own and are stored below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const setFilters = useCallback(
    (changes) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
      // Remember the result at once, so even an immediate navigation away keeps it.
      const next = new URLSearchParams(ownQuery);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      writeStored(storageKey, next.toString());
    },
    [setSearchParams, ownQuery, storageKey]
  );

  // Filters that arrive through the URL itself (a shared link, the browser's Back) are remembered too.
  useEffect(() => {
    if (ownQuery) writeStored(storageKey, ownQuery);
  }, [ownQuery, storageKey]);

  const clearFilters = useCallback(
    () => setFilters(Object.fromEntries(keys.map((key) => [key, '']))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFilters, keyList]
  );

  return [filters, setFilters, clearFilters];
}
